"use client";

import React, { useEffect, useRef, useState } from "react";
import { PenTool, FileText, Layers, DraftingCompass, PackageCheck, CircleCheck, Clock, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const stages = [{
  title: "Site Audit & GISTM Gap Analysis",
  description: "Comprehensive review of existing tailings facilities against GISTM and ICMM international standards.",
  duration: "2-4 Weeks",
  icon: FileText,
  deliverables: ["Consequence Classification", "GISTM Compliance Roadmap", "Critical Risk Register"]
}, {
  title: "Strategic Governance Framework",
  description: "Developing the organizational structure and accountability systems for long-term geotechnical safety.",
  duration: "1 Month",
  icon: DraftingCompass,
  deliverables: ["EOR Governance Manual", "Roles & Responsibilities Matrix", "Emergency Response Plans"]
}, {
  title: "Digital Transformation & Monitoring",
  description: "Integrating advanced environmental monitoring and geotechnical data into a centralized oversight system.",
  duration: "3-6 Months",
  icon: Layers,
  deliverables: ["Real-time Piezometer Integration", "InSAR Displacement Analysis", "Environmental Data Portal"]
}, {
  title: "Field Implementation & Support",
  description: "Execution of construction oversight, water balance management, and geotechnical field investigations.",
  duration: "Ongoing",
  icon: PackageCheck,
  deliverables: ["QA/QC Construction Reports", "Water Reduction Strategies", "Seepage Control Measures"]
}, {
  title: "EOR Stewardship & Compliance",
  description: "Final validation and ongoing Engineer of Record services to ensure continuous facility integrity.",
  duration: "Annual",
  icon: CircleCheck,
  deliverables: ["Annual Performance Reviews", "DSI/DSR Documentation", "IRTB Board Presentations"]
}];
export default function TheBlueprintJourney() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const element = sectionRef.current;
      const elementTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const startPoint = elementTop - windowHeight * 0.6;
      const endPoint = elementTop + elementHeight - windowHeight * 0.6;
      let progress = (scrollY - startPoint) / (endPoint - startPoint);
      progress = Math.max(0, Math.min(1, progress));
      setScrollProgress(progress * 100);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="64807" id="the-implementation-roadmap" ref={sectionRef} className="relative py-24 overflow-hidden bg-gradient-to-b from-background to-card text-foreground">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 backdrop-blur-sm rounded-sm font-mono uppercase tracking-widest">
            Program Development
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-6 text-foreground uppercase">
            The Implementation Roadmap
          </h2>
          <p className="text-lg text-secondary font-sans">
            A structured geotechnical workflow ensuring AGT Engineering and Consulting clients achieve global compliance and operational stability.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2 h-full">
            {}
            <div className="absolute top-0 left-0 w-full bg-primary shadow-[0_0_15px_var(--color-primary)] transition-all duration-100 ease-linear" style={{
            height: `${scrollProgress}%`
          }} />
          </div>

          <div className="space-y-12 md:space-y-24">
            {stages.map((stage, index) => {
            const isEven = index % 2 === 0;
            const threshold = index / (stages.length - 1) * 90;
            const isActive = scrollProgress > threshold;
            return <div key={index} data-index={index} className={cn("relative flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-0 transition-opacity duration-700", isActive ? "opacity-100" : "opacity-40")}>
                  {}
                  <div className="absolute left-8 md:left-1/2 -translate-x-1/2 flex items-center justify-center z-10">
                    <div className={cn("w-12 h-12 rounded-sm border flex items-center justify-center transition-all duration-500 bg-card", isActive ? "border-primary text-primary shadow-[0_0_15px_rgba(var(--primary),0.2)] scale-110" : "border-border text-muted-foreground")}>
                      <stage.icon className="w-5 h-5" />
                    </div>
                  </div>

                  {}
                  <div className={cn("w-full md:w-1/2 pl-24 md:pl-0 md:pr-16 transition-all duration-700 delay-100", isEven ? "md:text-right" : "md:order-last md:pl-16 md:text-left", isActive ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
                    <Card className="bg-card/40 backdrop-blur-md border-border hover:border-primary/40 transition-colors duration-300 overflow-hidden group rounded-sm">
                      <CardHeader className={cn("pb-4", isEven ? "md:items-end" : "md:items-start")}>
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="secondary" className="font-mono text-[10px] tracking-widest uppercase rounded-sm bg-muted/50 border-border">
                            PHASE 0{index + 1}
                          </Badge>
                          <Badge variant="outline" className="gap-1.5 bg-background/30 border-border font-mono text-[10px] rounded-sm">
                            <Clock className="w-3 h-3" />
                            {stage.duration}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl md:text-2xl text-foreground group-hover:text-primary transition-colors font-bold tracking-tight uppercase">
                          {stage.title}
                        </CardTitle>
                        <CardDescription className="text-base font-sans text-secondary">
                          {stage.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className={cn("flex flex-col gap-2", isEven ? "md:items-end" : "md:items-start")}>
                          <p className="text-[10px] font-bold text-primary mb-1 uppercase tracking-widest font-mono">
                            Technical Deliverables
                          </p>
                          <ul className={cn("space-y-1.5", isEven ? "md:text-right" : "md:text-left")}>
                            {stage.deliverables.map((item, i) => <li key={i} className="text-xs text-secondary font-mono flex items-center gap-2 md:inline-flex md:ml-4">
                                <CircleCheck className="w-3 h-3 text-primary shrink-0 md:hidden" />
                                <span>{item}</span>
                                <CircleCheck className={cn("w-3 h-3 text-primary shrink-0 hidden md:block", isEven ? "order-last" : "order-first")} />
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {}
                  <div className="hidden md:block w-1/2" />
                </div>;
          })}
          </div>
        </div>

        <div className="mt-24 text-center">
          <div className="inline-flex flex-col items-center gap-4 p-10 rounded-sm bg-card/40 border border-border backdrop-blur-sm">
            <h3 className="text-2xl font-bold text-foreground uppercase tracking-tight">Initiate Geotechnical Oversight</h3>
            <p className="text-secondary max-w-md font-sans">
              Connect with our international experts to begin the transformation of your tailings management program.
            </p>
            <Button asChild size="lg" className="mt-2 text-sm font-bold uppercase tracking-widest px-10 h-14 rounded-sm shadow-[0_0_20px_rgba(var(--primary),0.2)] hover:shadow-[0_0_30px_rgba(var(--primary),0.4)] transition-all bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Consult Our Engineers <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}